import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { patientApi } from '../../api/patientApi';

// Async thunks
export const fetchPatientInvoices = createAsyncThunk(
  'patientBilling/fetchInvoices',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await patientApi.getInvoices(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoices');
    }
  }
);

export const fetchInvoiceDetail = createAsyncThunk(
  'patientBilling/fetchInvoiceDetail',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await patientApi.getInvoiceDetail(invoiceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch invoice details');
    }
  }
);

export const downloadInvoicePDF = createAsyncThunk(
  'patientBilling/downloadPDF',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await patientApi.downloadInvoicePDF(invoiceId);
      return { invoiceId, blob: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to download PDF');
    }
  }
);

const initialState = {
  invoices: {
    data: [],
    loading: false,
    error: null,
    pagination: null
  },
  currentInvoice: {
    data: null,
    loading: false,
    error: null
  },
  downloading: false,
  downloadError: null
};

const patientBillingSlice = createSlice({
  name: 'patientBilling',
  initialState,
  reducers: {
    clearCurrentInvoice: (state) => {
      state.currentInvoice.data = null;
      state.currentInvoice.error = null;
    },
    clearDownloadError: (state) => {
      state.downloadError = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch invoices
      .addCase(fetchPatientInvoices.pending, (state) => {
        state.invoices.loading = true;
        state.invoices.error = null;
      })
      .addCase(fetchPatientInvoices.fulfilled, (state, action) => {
        state.invoices.loading = false;
        state.invoices.data = action.payload.results;
        state.invoices.pagination = action.payload.pagination;
      })
      .addCase(fetchPatientInvoices.rejected, (state, action) => {
        state.invoices.loading = false;
        state.invoices.error = action.payload;
      })
      // Fetch invoice detail
      .addCase(fetchInvoiceDetail.pending, (state) => {
        state.currentInvoice.loading = true;
        state.currentInvoice.error = null;
      })
      .addCase(fetchInvoiceDetail.fulfilled, (state, action) => {
        state.currentInvoice.loading = false;
        state.currentInvoice.data = action.payload;
      })
      .addCase(fetchInvoiceDetail.rejected, (state, action) => {
        state.currentInvoice.loading = false;
        state.currentInvoice.error = action.payload;
      })
      // Download PDF
      .addCase(downloadInvoicePDF.pending, (state) => {
        state.downloading = true;
        state.downloadError = null;
      })
      .addCase(downloadInvoicePDF.fulfilled, (state, action) => {
        state.downloading = false;
        // Create download link and trigger download
        const { invoiceId, blob } = action.payload;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `invoice_${invoiceId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .addCase(downloadInvoicePDF.rejected, (state, action) => {
        state.downloading = false;
        state.downloadError = action.payload;
      });
  }
});

export const { clearCurrentInvoice, clearDownloadError } = patientBillingSlice.actions;
export default patientBillingSlice.reducer;
